import { formatLog } from "@themuse/design-system";
import { isBrowser } from "@themuse/design-system/lib/constants/browser";
import { InteractionType } from "../interfaces/context/appContextInterfaces";

/**
 * Retrieves the entire interaction history from local storage.
 *
 * @returns {InteractionType[]} - The interaction history array.
 */
export const getInteractionHistory = (): InteractionType[] => {
    try {
        const history = localStorage.getItem("jobInteractionHistory");
        if (!history) return [];
        const result = JSON.parse(history);
        if (Array.isArray(result)) return result;
        // we are malformed so kill this history
        window.localStorage.removeItem("jobInteractionHistory");
        return [];
    } catch (error) {
        const msg = "Job-Renderer was unable to parse the interactions from local storage.";
        console.error(msg, JSON.stringify(formatLog({ error })));
        return [];
    }
};

/**
 * Retrieves the most recent interaction for a specific job ID.
 *
 * @param {number} id - The Job ID of the event to retrieve.
 * @returns {InteractionType | null} - The event object if found, otherwise null.
 */
export const getJobLastInteraction = (id: number): InteractionType | null => getInteractionHistory()
    .find((x: InteractionType) => x.id === id) ?? null;

/**
 * Sets the interaction as viewed for a specific job ID if that job has not been applied to.
 *
 * @param {number} id - The Job ID of the interaction to set as viewed.
 * @returns {void}
 */
export const setInteractionViewed = (id: number): InteractionType => {
    const history = getInteractionHistory();
    const jobHistory = getJobLastInteraction(id);
    const viewedEvent = { id, dt: new Date().getTime(), app: false };

    const payload = [
        ...history.filter((x: any) => x.id !== id),
        viewedEvent
    ];

    if (!jobHistory?.app) {
        window.localStorage.setItem("jobInteractionHistory", JSON.stringify(payload));
        window.dispatchEvent(new Event("storage"));
        return viewedEvent;
    }
    return jobHistory;
};

export const getApplicant = () => {
    if (!isBrowser) return false;
    return !!localStorage.getItem("Applicant");
};

/**
 * Returns a formatted date string in the "month day, year" format.
 *
 * @param dt - A timestamp representing a date and time.
 * @returns A formatted date string representing the input timestamp in the "month day, year" format.
 *
 * @example
 * const dt = 1627896000000;
 * const date = getInteractionDate(dt);
 * console.log(date); // Output: "August 3, 2021"
 */
export const getInteractionDate = (dt: number) => new Date(dt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
});
